import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  CreditCard, Plus, Search, Edit2, Trash2, X, AlertTriangle, CheckCircle2,
  Loader, Calendar, DollarSign, Filter, ArrowDownToLine, RotateCcw, Eye,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const STATUS_BADGE = {
  aberto: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  vencido: 'bg-red-500/20 text-red-300 border-red-500/40',
  pago: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  cancelado: 'bg-gray-500/20 text-gray-400 border-gray-500/40',
};
const STATUS_LABEL = { aberto: 'Em aberto', vencido: 'Vencido', pago: 'Pago', cancelado: 'Cancelado' };

export default function ContasPagar() {
  const { user } = useAuth();
  const canEdit = !!(user?.is_master || user?.role === 'admin' || user?.role === 'gestor' || user?.editar_contas_pagar);

  const [contas, setContas] = useState([]);
  const [totais, setTotais] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cc, setCc] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);

  // filtros
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrigem, setFilterOrigem] = useState('all');
  const [filterCC, setFilterCC] = useState('all');
  const [dataInicio, setDataInicio] = useState(firstDayOfMonth());
  const [dataFim, setDataFim] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showPagModal, setShowPagModal] = useState(null);
  const [pagForm, setPagForm] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      if (filterStatus !== 'all') params.append('status_filtro', filterStatus);
      if (filterOrigem !== 'all') params.append('origem', filterOrigem);
      if (filterCC !== 'all') params.append('centro_de_custo_id', filterCC);
      const [contasR, totR] = await Promise.all([
        fetch(`${API_URL}/api/contas-pagar?${params}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/contas-pagar/totalizadores?${params}`, { headers: authHeaders() }),
      ]);
      if (contasR.ok) setContas(await contasR.json());
      if (totR.ok) setTotais(await totR.json());
    } catch (e) { toast.error('Erro ao carregar contas a pagar'); }
    finally { setLoading(false); }
  }, [dataInicio, dataFim, filterStatus, filterOrigem, filterCC]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    (async () => {
      const [ccR, fR] = await Promise.all([
        fetch(`${API_URL}/api/centros-custo?status=ativo`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/fornecedores`, { headers: authHeaders() }),
      ]);
      if (ccR.ok) setCc(await ccR.json());
      if (fR.ok) setFornecedores(await fR.json());
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      descricao: '', valor: '',
      data_vencimento: todayISO(),
      data_emissao: todayISO(),
      fornecedor_id: '', fornecedor_nome: '',
      centro_de_custo_id: '', categoria: '',
      numero_documento: '', observacoes: '',
    });
    setShowModal(true);
  };

  const openEdit = (c) => {
    if (c.status === 'pago') return toast.warning('Conta paga não pode ser editada — reverta o pagamento primeiro');
    setEditing(c);
    setForm({
      descricao: c.descricao, valor: c.valor,
      data_vencimento: c.data_vencimento, data_emissao: c.data_emissao || '',
      fornecedor_id: c.fornecedor_id || '', fornecedor_nome: c.fornecedor_nome || '',
      centro_de_custo_id: c.centro_de_custo_id || '',
      categoria: c.categoria || '',
      numero_documento: c.numero_documento || '',
      observacoes: c.observacoes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.descricao?.trim()) return toast.error('Descrição obrigatória');
    if (!Number(form.valor) || Number(form.valor) <= 0) return toast.error('Valor deve ser maior que zero');
    setSaving(true);
    try {
      const body = {
        ...form,
        valor: Number(form.valor),
        fornecedor_id: form.fornecedor_id || null,
        centro_de_custo_id: form.centro_de_custo_id || null,
      };
      const url = editing ? `${API_URL}/api/contas-pagar/${editing.id}` : `${API_URL}/api/contas-pagar`;
      const r = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro ao salvar'); }
      toast.success(editing ? 'Conta atualizada' : 'Conta lançada');
      setShowModal(false);
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Excluir conta "${c.descricao}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/contas-pagar/${c.id}`, { method: 'DELETE', headers: authHeaders() });
      if (r.ok) { toast.success('Conta excluída'); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
  };

  const openPagar = (c) => {
    const saldo = Math.max((c.valor || 0) - (c.valor_pago_principal_total || 0), 0);
    setShowPagModal(c);
    setPagForm({
      data_pagamento: todayISO(),
      modo: 'total',         // 'total' | 'parcial'
      valor_pago: saldo,     // principal a abater
      juros_multa: 0,
      desconto: 0,
      forma_pagamento: 'transferencia',
      observacoes: '',
    });
  };

  const _submitBaixa = async (conta, payload, endpoint) => {
    const r = await fetch(`${API_URL}/api/contas-pagar/${conta.id}/${endpoint}`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Erro ao registrar baixa');
    return d;
  };

  const handlePagar = async () => {
    const c = showPagModal;
    const f = pagForm;
    const isParcial = f.modo === 'parcial';
    const endpoint = isParcial ? 'baixa-parcial' : 'pagar';
    const valorField = isParcial ? 'valor_principal' : 'valor_pago';
    const buildPayload = (ignorar) => ({
      data_pagamento: f.data_pagamento,
      [valorField]: Number(f.valor_pago),
      juros_multa: Number(f.juros_multa) || 0,
      desconto: Number(f.desconto) || 0,
      forma_pagamento: f.forma_pagamento,
      observacoes: f.observacoes,
      ignorar_duplicidade: !!ignorar,
    });

    try {
      let d = await _submitBaixa(c, buildPayload(false), endpoint);
      if (d?.duplicate_alert?.has_duplicates) {
        const dupCount = d.duplicate_alert.count;
        const sample = (d.duplicate_alert.duplicates || [])
          .slice(0, 3)
          .map((x) => `• R$ ${Number(x.valor).toFixed(2)} em ${x.data}${x.fornecedor_nome ? ' — ' + x.fornecedor_nome : ''}`)
          .join('\n');
        const ok = window.confirm(
          `⚠️ Possível duplicidade detectada (${dupCount} lançamento(s) semelhante(s)):\n\n${sample}\n\nDeseja registrar mesmo assim?`,
        );
        if (!ok) return;
        d = await _submitBaixa(c, buildPayload(true), endpoint);
      }
      toast.success(isParcial ? 'Baixa parcial registrada' : 'Pagamento registrado');
      setShowPagModal(null);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleReverter = async (c) => {
    if (!window.confirm(`Reverter pagamento de "${c.descricao}"?`)) return;
    const r = await fetch(`${API_URL}/api/contas-pagar/${c.id}/reverter-pagamento`, {
      method: 'POST', headers: authHeaders(),
    });
    if (r.ok) { toast.success('Pagamento revertido'); fetchData(); }
    else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
  };

  return (
    <div className="space-y-6" data-testid="contas-pagar-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Contas a Pagar</h1>
          <p className="text-gray-400 mt-1">Obrigações financeiras agrupadas por status e origem</p>
        </div>
        {canEdit && (
          <button data-testid="cp-nova-conta" onClick={openCreate}
                  className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg">
            <Plus size={18} /> Nova Obrigação
          </button>
        )}
      </div>

      {/* Cards totalizadores */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <TotalCard label="Em Aberto" value={totais?.aberto} qtd={totais?.qtd_aberto} icon={Calendar} color="text-blue-400" testid="card-aberto" />
        <TotalCard label="Vencido" value={totais?.vencido} qtd={totais?.qtd_vencido} icon={AlertTriangle} color="text-red-400" highlight testid="card-vencido" />
        <TotalCard label="Pago no Período" value={totais?.pago_periodo} qtd={totais?.qtd_pago_periodo} icon={CheckCircle2} color="text-emerald-400" testid="card-pago" />
        <TotalCard label="Total do Período" value={totais?.total_periodo} icon={DollarSign} color="text-yellow-400" testid="card-total" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 bg-[#1A1A1A] border border-gray-800 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-500" />
          <span className="text-gray-400 text-xs uppercase">Filtros</span>
        </div>
        <div className="flex flex-wrap gap-2 flex-1">
          <input data-testid="cp-filter-data-inicio" type="date" value={dataInicio}
                 onChange={(e) => setDataInicio(e.target.value)}
                 className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          <input data-testid="cp-filter-data-fim" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
                 className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          <select data-testid="cp-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm">
            <option value="all">Todos status</option>
            <option value="aberto">Em aberto</option>
            <option value="vencido">Vencido</option>
            <option value="pago">Pago</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <select value={filterOrigem} onChange={(e) => setFilterOrigem(e.target.value)}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm">
            <option value="all">Toda origem</option>
            <option value="manual">Manual</option>
            <option value="entrada_almoxarifado">Almoxarifado</option>
          </select>
          <select value={filterCC} onChange={(e) => setFilterCC(e.target.value)}
                  className="px-3 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm">
            <option value="all">Todos os CC</option>
            {cc.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader className="animate-spin text-yellow-500" size={32} /></div>
      ) : contas.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CreditCard size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma conta encontrada para esses filtros.</p>
        </div>
      ) : (
        <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg overflow-x-auto" data-testid="cp-lista">
          <table className="w-full">
            <thead className="bg-[#0A0A0A] text-xs text-gray-400 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Vencimento</th>
                <th className="px-3 py-2 text-left">Descrição</th>
                <th className="px-3 py-2 text-left">Fornecedor</th>
                <th className="px-3 py-2 text-left">CC</th>
                <th className="px-3 py-2 text-center">Parc.</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {contas.map(c => (
                <tr key={c.id} className="border-t border-gray-800/50 hover:bg-gray-900/40" data-testid={`cp-row-${c.id}`}>
                  <td className="px-3 py-2 text-sm text-white">
                    {c.data_vencimento}
                    {c.status === 'aberto' && c.dias_para_vencer != null && (
                      <span className="block text-xs text-gray-500">
                        {c.dias_para_vencer < 0 ? `há ${-c.dias_para_vencer}d` :
                         c.dias_para_vencer === 0 ? 'hoje' : `em ${c.dias_para_vencer}d`}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-300">
                    {c.descricao}
                    {c.numero_documento && <span className="block text-xs text-gray-500">Doc: {c.numero_documento}</span>}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-300">{c.fornecedor_nome || '—'}</td>
                  <td className="px-3 py-2 text-sm text-gray-400">{c.centro_de_custo_nome || '—'}</td>
                  <td className="px-3 py-2 text-center text-xs text-gray-400">
                    {c.parcela_total > 1 ? `${c.parcela_numero}/${c.parcela_total}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-white font-mono">{formatBRL(c.valor)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_BADGE[c.status] || ''}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    {c.origem === 'entrada_almoxarifado' && (
                      <span className="block text-[10px] text-blue-400 mt-1">
                        <ArrowDownToLine size={9} className="inline" /> Almox
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {c.status !== 'pago' && canEdit && (
                        <button onClick={() => openPagar(c)}
                                data-testid={`cp-pagar-${c.id}`}
                                title="Marcar como pago"
                                className="p-1.5 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300">
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      {c.status === 'pago' && canEdit && (
                        <button onClick={() => handleReverter(c)}
                                data-testid={`cp-reverter-${c.id}`}
                                title="Reverter pagamento"
                                className="p-1.5 rounded bg-orange-500/10 hover:bg-orange-500/20 text-orange-300">
                          <RotateCcw size={14} />
                        </button>
                      )}
                      {canEdit && c.status !== 'pago' && (
                        <button onClick={() => openEdit(c)}
                                data-testid={`cp-edit-${c.id}`}
                                title="Editar"
                                className="p-1.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300">
                          <Edit2 size={14} />
                        </button>
                      )}
                      {canEdit && c.status !== 'pago' && (
                        <button onClick={() => handleDelete(c)}
                                data-testid={`cp-delete-${c.id}`}
                                title="Excluir"
                                className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-300">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal criar/editar */}
      {showModal && form && (
        <Modal title={editing ? 'Editar Obrigação' : 'Nova Obrigação'} onClose={() => setShowModal(false)} testid="cp-modal">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400">Descrição *</label>
              <input data-testid="cp-form-descricao" value={form.descricao}
                     onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Valor (R$) *</label>
                <input data-testid="cp-form-valor" type="number" min="0" step="0.01" value={form.valor}
                       onChange={(e) => setForm({ ...form, valor: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Vencimento *</label>
                <input data-testid="cp-form-venc" type="date" value={form.data_vencimento}
                       onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Fornecedor</label>
                <select value={form.fornecedor_id}
                        onChange={(e) => setForm({ ...form, fornecedor_id: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white">
                  <option value="">—</option>
                  {fornecedores.filter(f => f.ativo).map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400">Centro de Custo</label>
                <select value={form.centro_de_custo_id}
                        onChange={(e) => setForm({ ...form, centro_de_custo_id: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white">
                  <option value="">—</option>
                  {cc.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Categoria</label>
                <input value={form.categoria}
                       onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Nº Documento</label>
                <input value={form.numero_documento}
                       onChange={(e) => setForm({ ...form, numero_documento: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400">Observações</label>
              <textarea rows={2} value={form.observacoes}
                        onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg">Cancelar</button>
            <button data-testid="cp-form-save" disabled={saving} onClick={handleSave}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg disabled:opacity-50">
              {saving ? 'Salvando...' : (editing ? 'Atualizar' : 'Lançar')}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal pagar */}
      {showPagModal && pagForm && (() => {
        const saldo = Math.max((showPagModal.valor || 0) - (showPagModal.valor_pago_principal_total || 0), 0);
        const principal = Number(pagForm.valor_pago) || 0;
        const juros = Number(pagForm.juros_multa) || 0;
        const desc = Number(pagForm.desconto) || 0;
        const caixa = Math.max(principal + juros - desc, 0);
        return (
        <Modal title={`Registrar pagamento — ${showPagModal.descricao}`}
               onClose={() => setShowPagModal(null)} testid="cp-pagar-modal">
          <div className="space-y-3">
            {/* Saldo da conta */}
            <div className="text-xs text-gray-400 bg-[#0A0A0A] border border-gray-800 rounded p-2 flex justify-between">
              <span>Valor original: <strong className="text-white">{formatBRL(showPagModal.valor)}</strong></span>
              <span data-testid="cp-pagar-saldo">Saldo restante: <strong className="text-yellow-400">{formatBRL(saldo)}</strong></span>
            </div>

            {/* Toggle modo */}
            <div className="flex gap-2">
              <button type="button"
                onClick={() => setPagForm({ ...pagForm, modo: 'total', valor_pago: saldo })}
                data-testid="cp-pagar-modo-total"
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${pagForm.modo === 'total' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
                Baixa Total (quita)
              </button>
              <button type="button"
                onClick={() => setPagForm({ ...pagForm, modo: 'parcial', valor_pago: 0 })}
                data-testid="cp-pagar-modo-parcial"
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${pagForm.modo === 'parcial' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
                Baixa Parcial
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Data do Pagamento *</label>
                <input type="date" value={pagForm.data_pagamento}
                       onChange={(e) => setPagForm({ ...pagForm, data_pagamento: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Valor Principal (R$) *</label>
                <input data-testid="cp-pagar-valor" type="number" min="0" step="0.01" value={pagForm.valor_pago}
                       onChange={(e) => setPagForm({ ...pagForm, valor_pago: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Juros / Multa (R$)</label>
                <input data-testid="cp-pagar-juros" type="number" min="0" step="0.01" value={pagForm.juros_multa}
                       onChange={(e) => setPagForm({ ...pagForm, juros_multa: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Desconto (R$)</label>
                <input data-testid="cp-pagar-desconto" type="number" min="0" step="0.01" value={pagForm.desconto}
                       onChange={(e) => setPagForm({ ...pagForm, desconto: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
              </div>
            </div>

            {/* Valor de caixa calculado */}
            <div className="bg-emerald-500/5 border border-emerald-500/30 rounded p-2 text-sm flex justify-between items-center">
              <span className="text-gray-400">Valor efetivo no caixa:</span>
              <span data-testid="cp-pagar-caixa" className="text-emerald-300 font-bold text-lg">{formatBRL(caixa)}</span>
            </div>

            <div>
              <label className="text-xs text-gray-400">Forma de Pagamento</label>
              <select value={pagForm.forma_pagamento}
                      onChange={(e) => setPagForm({ ...pagForm, forma_pagamento: e.target.value })}
                      className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white">
                <option value="dinheiro">Dinheiro</option>
                <option value="transferencia">Transferência</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="cartao">Cartão</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Observações</label>
              <textarea rows={2} value={pagForm.observacoes}
                        onChange={(e) => setPagForm({ ...pagForm, observacoes: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white" />
            </div>

            {/* Histórico de baixas anteriores */}
            {showPagModal.baixas?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-gray-800 rounded p-2" data-testid="cp-pagar-historico">
                <p className="text-xs text-gray-400 uppercase mb-1">Baixas anteriores ({showPagModal.baixas.length})</p>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {showPagModal.baixas.map((b) => (
                    <li key={b.baixa_id} className="text-xs text-gray-300 flex justify-between">
                      <span>{b.data_pagamento} • {b.forma_pagamento || '—'}</span>
                      <span>{formatBRL(b.valor_caixa)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setShowPagModal(null)} className="px-4 py-2 bg-gray-800 text-white rounded-lg">Cancelar</button>
            <button data-testid="cp-pagar-confirm" onClick={handlePagar}
                    disabled={principal <= 0}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg">
              {pagForm.modo === 'parcial' ? 'Registrar Baixa Parcial' : 'Confirmar Pagamento'}
            </button>
          </div>
        </Modal>
        );
      })()}
    </div>
  );
}

function TotalCard({ label, value, qtd, icon: Icon, color, highlight, testid }) {
  return (
    <div className={`bg-[#1A1A1A] border ${highlight ? 'border-red-500/40' : 'border-gray-800'} rounded-lg p-4`}
         data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
        <Icon size={16} className={color} />
      </div>
      <p className={`text-2xl font-bold ${highlight ? 'text-red-400' : 'text-white'}`}>
        {value != null ? formatBRL(value) : '—'}
      </p>
      {qtd != null && <p className="text-xs text-gray-500 mt-1">{qtd} registro(s)</p>}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-[#111] border border-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-[#111]">
          <h3 className="text-white font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
